import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PlayerAvatar from "../components/PlayerAvatar";
import { MagnifyingGlass, UserPlus, Funnel, Star } from "@phosphor-icons/react";

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState("");
  const [position, setPosition] = useState("");
  const [team, setTeam] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data));
    api.get("/settings").then((r) => setSettings(r.data || {}));
  }, []);

  const positions = useMemo(() => {
    const s = new Set(players.map((p) => p.position).filter(Boolean));
    return Array.from(s);
  }, [players]);

  const filtered = useMemo(() => {
    return players.filter((p) => {
      const full = `${p.first_name} ${p.last_name}`.toLowerCase();
      const jn = String(p.jersey_number || "");
      const matchesQ = !q ||
        full.includes(q.toLowerCase()) ||
        jn.includes(q) ||
        (p.position || "").toLowerCase().includes(q.toLowerCase());
      const matchesPos = !position || p.position === position;
      const matchesTeam = !team || p.team === team;
      const matchesAge = !ageGroup || p.age_group === ageGroup;
      return matchesQ && matchesPos && matchesTeam && matchesAge;
    });
  }, [players, q, position, team, ageGroup]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Roster</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Players</h1>
        </div>
        <Link
          to="/players/new"
          data-testid="add-player-btn"
          className="h-14 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider inline-flex items-center gap-2"
        >
          <UserPlus size={22} weight="bold" /> Add Player
        </Link>
      </div>

      <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="players-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, jersey, position…"
              className="w-full h-12 pl-12 pr-4 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]"
            />
          </div>
          <select
            data-testid="filter-position"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md"
          >
            <option value="">All Positions</option>
            {positions.map((p) => <option key={p}>{p}</option>)}
          </select>
          <select
            data-testid="filter-team"
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            className="h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md"
          >
            <option value="">All Teams</option>
            {(settings.teams || []).map((t) => <option key={t}>{t}</option>)}
          </select>
          <select
            data-testid="filter-age-group"
            value={ageGroup}
            onChange={(e) => setAgeGroup(e.target.value)}
            className="h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md"
          >
            <option value="">All Age Groups</option>
            {(settings.age_groups || []).map((a) => <option key={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-10 text-center">
          <div className="overline">No players</div>
          <div className="text-neutral-400 mt-2">Add your first player to get started.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((p, idx) => (
            <Link
              key={p.id}
              to={`/players/${p.id}`}
              data-testid={`player-card-${p.id}`}
              className="bg-[#121212] border border-neutral-800 rounded-lg p-4 card-lift stagger-item flex items-center gap-4"
              style={{ animationDelay: `${idx * 20}ms` }}
            >
              <PlayerAvatar player={p} size={64} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <div className="font-display font-bold text-lg leading-tight truncate">
                    {p.first_name} {p.last_name}
                  </div>
                  {p.jersey_number ? (
                    <span className="text-[#D90429] font-display font-black">#{p.jersey_number}</span>
                  ) : null}
                </div>
                <div className="overline mt-1">{p.position || "—"}</div>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span className="text-[#06D6A0] font-bold">{p.attendance_pct}%</span>
                  <span className="flex items-center gap-1 text-[#FFD166] font-bold">
                    <Star size={12} weight="fill" /> {p.overall_rating || 0}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
